import {
  type Location,
  type NavigateFunction,
  type Params,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import PageError from "@/components/page-error";
import type { TabRoute } from "@/@types/tab-navigation";
import { AccessProvider } from "@/routes/access-provider";

import { TAB_NOT_FOUND_MESSAGE, TAB_NOT_FOUND_STATUS } from "./constants";
import { TabBar } from "./tab-bar";
import { useVisibleTabs } from "./use-visible-tabs";

interface TabbedRouteGroupProps {
  basePath: string;
  tabs: TabRoute[];
}

const NotFound = (): JSX.Element => (
  <PageError status={TAB_NOT_FOUND_STATUS} message={TAB_NOT_FOUND_MESSAGE} />
);

export const TabbedRouteGroup = ({ basePath, tabs }: TabbedRouteGroupProps): JSX.Element => {
  const visibleTabs: TabRoute[] = useVisibleTabs(tabs);
  const navigate: NavigateFunction = useNavigate();
  const params: Readonly<Params<string>> = useParams();
  const { pathname, search, hash }: Location = useLocation();
  const [firstTab]: TabRoute[] = visibleTabs;

  if (!firstTab) return <NotFound />;

  const activePath: string | undefined = params["*"]?.split("/")[0];
  const selectTab = (path: string): void => {
    const target: string = `${basePath}/${path}`;
    if (pathname !== target) navigate(target);
  };

  return (
    <div className="flex w-full flex-col">
      <div className="px-10 pt-6">
        <TabBar tabs={visibleTabs} activePath={activePath} onSelect={selectTab} />
      </div>
      <Routes>
        <Route index element={<Navigate to={`${basePath}/${firstTab.path}${search}${hash}`} replace />} />
        {tabs.map((tab: TabRoute) => (
          <Route
            key={tab.key}
            path={`${tab.path}/*`}
            element={
              <AccessProvider
                requiredRole={tab.requiredRole}
                requiredAction={tab.requiredAction}
                requiredGroup={tab.groupRequired}
              >
                {tab.element}
              </AccessProvider>
            }
          />
        ))}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
};
